import dayjs from 'dayjs';
import {
  COUPON_CODE_GROUP_SIZE,
  COUPON_CODE_LENGTH,
  COUPON_DEADLINE_IMMINENT_DAYS,
  COUPON_MESSAGES,
  FULL_DATE_FORMAT,
} from '@/constants';
import {CouponApplyType, CouponBlockedReason, CouponIssueRoute} from '@/enums';
import {MyCoupon, MyCouponHistory} from '@/interface';

/**
 * 입력값을 검증·전송용 원문으로 정규화한다.
 * 하이픈·공백·소문자를 모두 허용하고 영문 대문자와 숫자만 남긴다.
 */
export const normalizeCouponCode = (value: string) =>
  value
    .replace(/[^a-zA-Z0-9]/g, '')
    .toUpperCase()
    .slice(0, COUPON_CODE_LENGTH);

/** 원문을 화면 표기값 XXXXX-XXXXX 로 변환한다. */
export const formatCouponCode = (value: string) => {
  const code = normalizeCouponCode(value);

  if (code.length <= COUPON_CODE_GROUP_SIZE) {
    return code;
  }

  return `${code.slice(0, COUPON_CODE_GROUP_SIZE)}-${code.slice(
    COUPON_CODE_GROUP_SIZE
  )}`;
};

/**
 * 형식 검증. 정규화 후 영문 대문자·숫자 10자리인지만 확인한다.
 * 허용 문자셋 자체의 검증은 서버가 담당한다.
 */
export const isValidCouponCodeFormat = (value: string) =>
  new RegExp(`^[A-Z0-9]{${COUPON_CODE_LENGTH}}$`).test(
    normalizeCouponCode(value)
  );

const COUPON_ISSUE_ROUTE_LABEL: Record<CouponIssueRoute, string> = {
  [CouponIssueRoute.PhoneGrant]: '전화번호 지급 쿠폰',
  [CouponIssueRoute.ShareCode]: '공유 코드 쿠폰',
  [CouponIssueRoute.UniqueCode]: '개별 코드 쿠폰',
};

/** 배지 문구 */
export const getCouponIssueRouteLabel = (issueRoute: CouponIssueRoute) =>
  COUPON_ISSUE_ROUTE_LABEL[issueRoute] ?? '공유 코드 쿠폰';

/** 같은 쿠폰이 여러 장이면 `이름 (1)`. GA coupon_name 에는 번호 없는 name 을 쓴다 */
export const getCouponDisplayName = ({
  name,
  sequence,
}: {
  name: string;
  sequence?: number | null;
}) => (sequence ? `${name} (${sequence})` : name);

/** GA code_type 파라미터 (shared/unique) */
export const getCouponCodeType = (issueRoute: CouponIssueRoute) =>
  issueRoute === CouponIssueRoute.UniqueCode ? 'unique' : 'shared';

/** GA source 파라미터 (grant/coupon) */
export const getCouponSource = (issueRoute: CouponIssueRoute) =>
  issueRoute === CouponIssueRoute.PhoneGrant ? 'grant' : 'coupon';

const formatDate = (date?: string | null) =>
  date ? dayjs(date).format(FULL_DATE_FORMAT) : '';

/** `2026.09.15까지 등록·시작` */
export const getCouponDeadlineText = (coupon: MyCoupon) =>
  `${formatDate(coupon.registerDeadline)}까지 등록·시작`;

/** 등록 만료까지 남은 일수 (D-day) */
export const getCouponDeadlineDDay = (coupon: MyCoupon) =>
  dayjs(coupon.registerDeadline).endOf('day').diff(dayjs(), 'day');

/** 등록 만료 D-7 이내 여부 */
export const isCouponDeadlineImminent = (coupon: MyCoupon) => {
  const dDay = getCouponDeadlineDDay(coupon);

  return dDay >= 0 && dDay <= COUPON_DEADLINE_IMMINENT_DAYS;
};

/** 목록 카드 본문의 이용 기간 문구 */
export const getCouponPeriodText = (coupon: MyCoupon) => {
  if (coupon.applyType === CouponApplyType.Relative) {
    return `선택하면 오늘부터 ${coupon.durationDays}일간 이용할 수 있어요.`;
  }

  return `${formatDate(coupon.useBeganAt)}부터 ${formatDate(
    coupon.useEndedAt
  )}까지 이용할 수 있어요.`;
};

/** 받침 여부에 따라 목적격 조사를 붙인다 */
const withObjectJosa = (word: string) => {
  const lastChar = word.trim().slice(-1).charCodeAt(0);
  const isHangul = lastChar >= 0xac00 && lastChar <= 0xd7a3;
  const hasFinalConsonant = isHangul && (lastChar - 0xac00) % 28 !== 0;

  return `${word}${hasFinalConsonant ? '을' : '를'}`;
};

/**
 * 지금 시작할 경우의 이용 종료 예정일.
 * 상대기간형은 `오늘 + N일`, 기간고정형은 쿠폰의 종료일로 고정된다.
 */
export const getCouponExpectedEndDate = (coupon: MyCoupon) =>
  coupon.applyType === CouponApplyType.Relative
    ? dayjs()
        .add(coupon.durationDays ?? 0, 'day')
        .format(FULL_DATE_FORMAT)
    : formatDate(coupon.useEndedAt);

/**
 * 시작 확인창 본문.
 * 예) `스마트상점 30일 쿠폰을 오늘부터 30일간 사용합니다. 종료 예정일은 2026.09.08입니다.`
 */
export const getCouponConfirmMessage = (coupon: MyCoupon) => {
  const period =
    coupon.applyType === CouponApplyType.Relative
      ? `오늘부터 ${coupon.durationDays}일간 사용합니다.`
      : '오늘부터 사용합니다.';

  return `${withObjectJosa(coupon.name)} ${period} 종료 예정일은 ${getCouponExpectedEndDate(coupon)}입니다.`;
};

/** 아직 시작할 수 없는 기간고정형 쿠폰의 강조 문구 */
export const getCouponBlockedText = (coupon: MyCoupon) => {
  if (coupon.blockedReason !== CouponBlockedReason.NotStarted) {
    return '';
  }

  return `${formatDate(coupon.useBeganAt)}부터 사용할 수 있어요`;
};

/**
 * 이용 중인 이용권·쿠폰 보유 여부.
 * me 의 tier 는 만료 후 자정 배치까지 남아 있을 수 있어, 목록이 있으면 서버 판정을 우선한다.
 */
export const hasActiveUsePass = (
  coupons: MyCoupon[] | undefined,
  isPaidUser: boolean
) => {
  if (!coupons?.length) {
    return isPaidUser;
  }

  const blockedByPass = coupons.some(
    (i) => i.blockedReason === CouponBlockedReason.AlreadyUsingPass
  );

  return blockedByPass || (isPaidUser && !coupons.some((i) => i.usableNow));
};

/**
 * 현재 이용권 종료일이 쿠폰의 등록 만료일보다 늦은 경우 노출하는 경고 문구.
 * 등록은 허용하되 자동 연장·예약 시작은 하지 않는다.
 */
export const getCouponDeadlineWarning = (
  coupon: MyCoupon,
  passEndedAt?: string
) => {
  if (!passEndedAt) {
    return '';
  }

  return dayjs(passEndedAt).isAfter(dayjs(coupon.registerDeadline))
    ? COUPON_MESSAGES.deadlineBeforePassEnd
    : '';
};

/**
 * 사용을 시작한 적이 있는 이력만 남기고 시작일 최신순으로 정렬한다.
 * 등록만 하고 시작하지 않은 미사용만료 건은 고객 화면에서 제외한다(PRD 정책7).
 */
export const getActivatedCouponHistory = (items?: MyCouponHistory[]) =>
  (items ?? [])
    .filter((i) => !!i.useBeganAt)
    .sort((a, b) => dayjs(b.useBeganAt).valueOf() - dayjs(a.useBeganAt).valueOf());

/**
 * 이용 이력의 이용 기간 문구.
 * 중도 회수된 건은 종료 예정일 대신 실제 회수일까지만 표시한다.
 */
export const getCouponHistoryPeriodText = (item: MyCouponHistory) => {
  const endedAt = item.releasedAt || item.useEndedAt;

  if (!item.useBeganAt || !endedAt) {
    return '-';
  }

  return `${formatDate(item.useBeganAt)}~${formatDate(endedAt)}`;
};
